import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { driverProfileName } from '@/lib/driverProfile';
import { coverExpiryStatus } from '@/lib/coverExpiry';

const ITEMS_PER_PAGE = 10;

export default function RoadworthyRecords() {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const pagination = useServerPagination({ totalCount: 0, itemsPerPage: ITEMS_PER_PAGE });

    const getSortColumn = (key: string | null): string | null => {
        const nonSortableColumns = ['vehicleName', 'driverName', 'status'];
        if (key && nonSortableColumns.includes(key)) return null;
        return key || 'expires_at';
    };

    const { sortKey, sortDirection, handleSort } = useTableSort({
        defaultSortKey: 'expires_at',
        defaultSortDirection: 'asc',
        onSortChange: pagination.resetPage,
    });

    // Good through its expiry date, on the Ghana day (G6): the one rule, in `coverExpiryStatus`.
    const getStatus = (date: string) => coverExpiryStatus(date) ?? 'valid';

    const { data: allRoadworthiesForFilter } = useQuery({
        queryKey: ['roadworthies-filter-data'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicle_roadworthiness')
                .select(`id, expires_at, contracts(drivers(profiles(id, admin_id, first_name, last_name)), vehicles(make, model, vehicle_registrations(number)))`);
            if (error) throw error;
            return data;
        },
    });

    const getFilteredRoadworthyIds = () => {
        if (!allRoadworthiesForFilter) return null;
        if (!debouncedSearch && statusFilter === 'all') return null;
        return allRoadworthiesForFilter
            .filter(rw => {
                if (debouncedSearch) {
                    const searchLower = debouncedSearch.toLowerCase();
                    // The name searched is the name shown — the driver's own row, never an
                    // admin's test profile against the same driver id. See `driverProfileOf`.
                    const driverName = (driverProfileName(rw.contracts?.drivers?.profiles) ?? '').toLowerCase();
                    const matchesSearch = rw.contracts?.vehicles?.make?.toLowerCase().includes(searchLower) || rw.contracts?.vehicles?.model?.toLowerCase().includes(searchLower) || driverName.includes(searchLower);
                    if (!matchesSearch) return false;
                }
                if (statusFilter !== 'all') {
                    const status = getStatus(rw.expires_at);
                    if (status !== statusFilter) return false;
                }
                return true;
            })
            .map(rw => rw.id);
    };

    const filteredRoadworthyIds = getFilteredRoadworthyIds();

    const { data: countData } = useQuery({
        queryKey: ['roadworthies-count', debouncedSearch, statusFilter, filteredRoadworthyIds],
        queryFn: async () => {
            if (filteredRoadworthyIds !== null && filteredRoadworthyIds.length === 0) return 0;
            let query = supabase.from('vehicle_roadworthiness').select('*', { count: 'exact', head: true });
            if (filteredRoadworthyIds !== null) query = query.in('id', filteredRoadworthyIds);
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
        enabled: allRoadworthiesForFilter !== undefined,
    });

    const paginationWithCount = useServerPagination({ totalCount: countData || 0, itemsPerPage: ITEMS_PER_PAGE });

    const { data: roadworthies, isLoading } = useQuery({
        queryKey: ['roadworthies', paginationWithCount.currentPage, sortKey, sortDirection, debouncedSearch, statusFilter, filteredRoadworthyIds],
        queryFn: async () => {
            if (filteredRoadworthyIds !== null && filteredRoadworthyIds.length === 0) return [];
            const sortColumn = getSortColumn(sortKey);
            let query = supabase.from('vehicle_roadworthiness').select(`*, contracts(drivers(profiles(id, admin_id, first_name, last_name)), vehicles(make, model, vehicle_registrations(number)))`);
            if (sortColumn) query = query.order(sortColumn, { ascending: sortDirection === 'asc' });
            else query = query.order('expires_at', { ascending: true });
            if (filteredRoadworthyIds !== null) query = query.in('id', filteredRoadworthyIds);
            query = query.range(paginationWithCount.from, paginationWithCount.to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined && allRoadworthiesForFilter !== undefined,
    });

    useEffect(() => { paginationWithCount.resetPage(); }, [statusFilter, debouncedSearch]);

    const displayRoadworthies = roadworthies ?? [];

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search certificates..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="valid">Valid</SelectItem>
                            <SelectItem value="expiring">Expiring Soon</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : (
                    <>
                        <ListPagination currentPage={paginationWithCount.currentPage} totalPages={paginationWithCount.totalPages} totalItems={paginationWithCount.totalItems} onPageChange={paginationWithCount.goToPage} />
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <SortableTableHead>Vehicle</SortableTableHead>
                                        <SortableTableHead>Driver</SortableTableHead>
                                        <SortableTableHead sortKey="expires_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>Expiry Date</SortableTableHead>
                                        <SortableTableHead>Status</SortableTableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {displayRoadworthies.map((rw) => {
                                        const vehicleReg = rw.contracts?.vehicles?.vehicle_registrations;
                                        const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
                                        const driverName = driverProfileName(rw.contracts?.drivers?.profiles) ?? 'N/A';
                                        const status = getStatus(rw.expires_at);
                                        return (
                                            <TableRow key={rw.id}>
                                                <TableCell>
                                                    <div>
                                                        <span className="font-medium">{regNumber || 'Unregistered'}</span>
                                                        <p className="text-xs text-muted-foreground">{rw.contracts?.vehicles?.make} {rw.contracts?.vehicles?.model}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{driverName}</TableCell>
                                                <TableCell>{new Date(rw.expires_at).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    {status === 'expired' ? (
                                                        <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Expired</Badge>
                                                    ) : status === 'expiring' ? (
                                                        <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700">Expiring Soon</Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Valid</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {displayRoadworthies.length === 0 && !isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                                                {search || statusFilter !== 'all' ? 'No certificates match your filters' : 'No roadworthy certificates found'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
